import { useEffect, useRef, useState, useCallback } from "react";

import { theaterStore, speedStore, ambientStore } from "../stores/preferences";

const REST_DELAY_MS = 3000;
const SEEK_STEP_SECONDS = 10;
const VOLUME_STEP = 0.1;

/**
 * usePlayerControls — every piece of state the custom control chrome needs.
 *
 * State here changes on discrete user actions (a click, a key press), never per
 * frame — per-frame values (current time, progress) are handled imperatively by
 * ProgressBar/TimeDisplay so the React tree stays still during playback.
 */
export default function usePlayerControls({ videoRef, rootRef }) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [speed, setSpeedState] = useState(speedStore.read);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPip, setIsPip] = useState(false);
    const [isTheater, setIsTheater] = useState(theaterStore.read);
    const [isAmbient, setIsAmbient] = useState(ambientStore.read);
    const [isRest, setIsRest] = useState(false);
    const [flash, setFlash] = useState(null);

    const restTimerRef = useRef(null);
    const clickTimerRef = useRef(null);
    const flashIdRef = useRef(0);

    // On-video feedback pulse ("-10s", play/pause icon). Keyed by id so a
    // repeated action restarts the CSS animation.
    const showFlash = useCallback((kind, text = null) => {
        flashIdRef.current += 1;
        setFlash({ id: flashIdRef.current, kind, text });
    }, []);

    const pipSupported =
        typeof document !== "undefined" && !!document.pictureInPictureEnabled;

    // ── Activity / rest mode ─────────────────────────────────────────────
    const handleActivity = useCallback(() => {
        if (restTimerRef.current) clearTimeout(restTimerRef.current);
        setIsRest(false);
        restTimerRef.current = setTimeout(() => setIsRest(true), REST_DELAY_MS);
    }, []);

    // Leaving the player should tuck the chrome away immediately, not after
    // the idle delay.
    const handlePointerLeave = useCallback(() => {
        if (restTimerRef.current) clearTimeout(restTimerRef.current);
        setIsRest(true);
    }, []);

    useEffect(() => {
        handleActivity();
        return () => {
            if (restTimerRef.current) clearTimeout(restTimerRef.current);
            if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
        };
    }, [handleActivity]);

    // ── Playback ─────────────────────────────────────────────────────────
    const togglePlay = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused || video.ended) {
            video.play().catch(() => { });
            showFlash("play");
        } else {
            video.pause();
            showFlash("pause");
        }
    }, [videoRef, showFlash]);

    const seekBy = useCallback((seconds) => {
        const video = videoRef.current;
        if (!video || !video.duration) return;
        video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
        showFlash(seconds < 0 ? "back" : "forward", `${seconds > 0 ? "+" : ""}${seconds}s`);
    }, [videoRef, showFlash]);

    // ── Volume ───────────────────────────────────────────────────────────
    const changeVolume = useCallback((value) => {
        const video = videoRef.current;
        if (!video) return;
        const clamped = Math.max(0, Math.min(1, value));
        video.volume = clamped;
        video.muted = clamped === 0;
    }, [videoRef]);

    const toggleMute = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = !video.muted;
    }, [videoRef]);

    // ── Speed ────────────────────────────────────────────────────────────
    const setSpeed = useCallback((rate) => {
        const video = videoRef.current;
        if (video) video.playbackRate = rate;
        speedStore.write(rate);
        setSpeedState(rate);
    }, [videoRef]);

    // ── Fullscreen ───────────────────────────────────────────────────────
    // Requested on the document root, not the player: navigating to the
    // next/previous video remounts the player, and fullscreen on an element
    // dies with it. The document survives navigation, and the player overlays
    // it (fixed positioning) while fullscreen is active — so the new video
    // keeps playing fullscreen.
    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.().catch(() => { });
        } else {
            document.exitFullscreen?.();
        }
    }, []);

    // ── Picture-in-picture ───────────────────────────────────────────────
    const togglePip = useCallback(async () => {
        const video = videoRef.current;
        if (!video || !pipSupported) return;
        try {
            if (document.pictureInPictureElement) await document.exitPictureInPicture();
            else await video.requestPictureInPicture();
        } catch (error) {
            console.error("Erro ao alternar picture-in-picture:", error);
        }
    }, [videoRef, pipSupported]);

    // ── Theater mode ─────────────────────────────────────────────────────
    const toggleTheater = useCallback(() => {
        theaterStore.write(!theaterStore.read());
    }, []);

    useEffect(() => theaterStore.subscribe(setIsTheater), []);

    // ── Ambient glow ("iluminação cinematográfica") ──────────────────────
    const toggleAmbient = useCallback(() => {
        ambientStore.write(!ambientStore.read());
    }, []);

    // Called by AmbientGlow's FPS guard when the effect is degrading playback.
    const disableAmbientForPerformance = useCallback(() => {
        ambientStore.write(false);
        window.setSnackbar?.(
            "A iluminação cinematográfica foi desabilitada para acelerar a performance da sua experiência de vídeo",
            "info"
        );
    }, []);

    useEffect(() => ambientStore.subscribe(setIsAmbient), []);

    // ── Stage click: single toggles play, double toggles fullscreen ──────
    const handleStageClick = useCallback(() => {
        if (clickTimerRef.current) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
            toggleFullscreen();
            return;
        }
        clickTimerRef.current = setTimeout(() => {
            clickTimerRef.current = null;
            togglePlay();
        }, 250);
    }, [togglePlay, toggleFullscreen]);

    // ── Keyboard shortcuts (page-wide, YouTube-style) ────────────────────
    const handleKeyDown = useCallback((event) => {
        const video = videoRef.current;
        if (!video) return;

        // Never steal keys from form fields, editors or modified combos.
        const target = event.target;
        const tagName = target?.tagName;
        if (tagName === "INPUT" || tagName === "TEXTAREA" || target?.isContentEditable) return;
        if (event.ctrlKey || event.metaKey || event.altKey) return;

        const actions = {
            " ": togglePlay,
            k: togglePlay,
            ArrowLeft: () => seekBy(-SEEK_STEP_SECONDS),
            ArrowRight: () => seekBy(SEEK_STEP_SECONDS),
            ArrowUp: () => changeVolume(video.volume + VOLUME_STEP),
            ArrowDown: () => changeVolume(video.volume - VOLUME_STEP),
            m: toggleMute,
            f: toggleFullscreen,
            t: toggleTheater,
        };

        const action = actions[event.key.length === 1 ? event.key.toLowerCase() : event.key];
        if (!action) return;

        event.preventDefault();
        action();
        handleActivity();
    }, [videoRef, togglePlay, seekBy, changeVolume, toggleMute, toggleFullscreen, toggleTheater, handleActivity]);

    // Attach the shortcuts to the document while the player is mounted, so
    // they work without requiring the player to hold focus.
    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    // ── Mirror media element state into React (discrete changes only) ────
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleVolumeChange = () => {
            setVolume(video.volume);
            setMuted(video.muted);
        };
        const handleRateChange = () => setSpeedState(video.playbackRate);
        const handleEnterPip = () => setIsPip(true);
        const handleLeavePip = () => setIsPip(false);

        // A remembered speed must survive source swaps, which reset playbackRate.
        const handleLoadedMetadata = () => {
            const remembered = speedStore.read();
            if (video.playbackRate !== remembered) video.playbackRate = remembered;
        };

        const handleFullscreenChange = () =>
            setIsFullscreen(!!document.fullscreenElement);

        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        video.addEventListener("volumechange", handleVolumeChange);
        video.addEventListener("ratechange", handleRateChange);
        video.addEventListener("enterpictureinpicture", handleEnterPip);
        video.addEventListener("leavepictureinpicture", handleLeavePip);
        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        document.addEventListener("fullscreenchange", handleFullscreenChange);

        handleVolumeChange();
        handleLoadedMetadata();
        // A remount mid-navigation must rejoin an already-active fullscreen.
        handleFullscreenChange();

        return () => {
            video.removeEventListener("play", handlePlay);
            video.removeEventListener("pause", handlePause);
            video.removeEventListener("volumechange", handleVolumeChange);
            video.removeEventListener("ratechange", handleRateChange);
            video.removeEventListener("enterpictureinpicture", handleEnterPip);
            video.removeEventListener("leavepictureinpicture", handleLeavePip);
            video.removeEventListener("loadedmetadata", handleLoadedMetadata);
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, [videoRef, rootRef]);

    return {
        state: { isPlaying, volume, muted, speed, isFullscreen, isPip, pipSupported, isTheater, isAmbient, isRest, flash },
        actions: {
            togglePlay,
            seekBy,
            changeVolume,
            toggleMute,
            setSpeed,
            toggleFullscreen,
            togglePip,
            toggleTheater,
            toggleAmbient,
            disableAmbientForPerformance,
            handleStageClick,
            handleActivity,
            handlePointerLeave,
        },
    };
}
